import { NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  ViewEncapsulation,
  inject,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatMenuModule } from '@angular/material/menu';
import {
  FuseNavigationService,
  FuseVerticalNavigationComponent,
} from '@fuse/components/navigation';
import { AvailableLangs, TranslocoService } from '@ngneat/transloco';
import { take } from 'rxjs';

@Component({
  selector: 'languages',
  templateUrl: './languages.component.html',
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  exportAs: 'languages',
  imports: [MatButtonModule, MatMenuModule, NgTemplateOutlet],
})
export class LanguagesComponent implements OnInit, OnDestroy {
  private _changeDetectorRef = inject(ChangeDetectorRef);
  private _fuseNavigationService = inject(FuseNavigationService);
  private _translocoService = inject(TranslocoService);

  availableLangs: AvailableLangs;
  activeLang: string;
  flagCodes: any;

  /** Inserted by Angular inject() migration for backwards compatibility */
  constructor(...args: unknown[]);

  /**
   * Constructor
   */
  constructor() {}

  // -----------------------------------------------------------------------------------------------------
  // @ Lifecycle hooks
  // -----------------------------------------------------------------------------------------------------

  /**
   * On init
   */
  ngOnInit(): void {
    // Get the available languages from transloco
    this.availableLangs = this._translocoService.getAvailableLangs();

    // Set initial language state
    this.activeLang = this._translocoService.getActiveLang();
    this._translocoService
      .load(this.activeLang)
      .pipe(take(1))
      .subscribe(() => {
        this._updateNavigation(this.activeLang);
        this._changeDetectorRef.markForCheck();
      });

    // Subscribe to language changes
    this._translocoService.langChanges$.subscribe(activeLang => {
      // Get the active lang
      this.activeLang = activeLang;

      // Update the navigation after translations load
      this._translocoService
        .load(activeLang)
        .pipe(take(1))
        .subscribe(() => {
          this._updateNavigation(activeLang);
          this._changeDetectorRef.markForCheck();
        });
    });

    // Set the country iso codes for languages for flags
    this.flagCodes = {
      en: 'en',
      ru: 'ru',
      ae: 'ae',
      ir: 'ir',
      zh: 'cn',
      in: 'in',
      ur: 'pk',
      ta: 'in',
      ml: 'in',
      tl: 'ph',
    };
  }

  /**
   * On destroy
   */
  ngOnDestroy(): void {}

  // -----------------------------------------------------------------------------------------------------
  // @ Public methods
  // -----------------------------------------------------------------------------------------------------

  /**
   * Set the active lang
   *
   * @param lang
   */
  setActiveLang(lang: string): void {
    // Set the active lang
    this._translocoService.setActiveLang(lang);
    localStorage.setItem('preferredLanguage', lang);
  }

  /**
   * Track by function for ngFor loops
   *
   * @param index
   * @param item
   */
  trackByFn(index: number, item: any): any {
    return item.id || index;
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Private methods
  // -----------------------------------------------------------------------------------------------------

  /**
   * Update the navigation
   *
   * @param lang
   * @private
   */
  private _updateNavigation(lang: string): void {
    // Get the component -> navigation data -> item
    const navComponent =
      this._fuseNavigationService.getComponent<FuseVerticalNavigationComponent>(
        'mainNavigation'
      );

    // Return if the navigation component does not exist
    if (!navComponent) {
      return null;
    }

    // Get the flat navigation data
    const navigation = navComponent.navigation ?? [];

    const navTranslations: {
      id: string;
      titleKey: string;
      subtitleKey?: string;
    }[] = [
      { id: 'Dashboard', titleKey: 'nav.dashboard' },
      { id: 'home.inbox', titleKey: 'nav.inbox' },
      {
        id: 'sales.quote-requests.old',
        titleKey: 'nav.quoteRequests',
        subtitleKey: 'nav.manageQuotes',
      },
      { id: 'sales.quote-requests.new', titleKey: 'nav.inProgressQuotes' },
      {
        id: 'sales.quote-requests.archived',
        titleKey: 'nav.archivedQuotes',
      },
      {
        id: 'dashboards',
        titleKey: 'nav.shipments',
        subtitleKey: 'nav.shipmentsSubtitle',
      },
      {
        id: 'sales.quote-requests.operationInProgress',
        titleKey: 'nav.inProgressShipments',
      },
      {
        id: 'operations.completed',
        titleKey: 'nav.completedShipments',
      },
      {
        id: 'accounting',
        titleKey: 'nav.myFinances',
        subtitleKey: 'nav.myFinancesSubtitle',
      },
      { id: 'accounting.invoice.list', titleKey: 'nav.invoices' },
      {
        id: 'accounting.receipt-voucher.list',
        titleKey: 'nav.receiptVouchers',
      },
    ];

    navTranslations.forEach(item => {
      const navItem = this._fuseNavigationService.getItem(item.id, navigation);
      if (!navItem) {
        return;
      }

      navItem.title = this._translocoService.translate(item.titleKey);
      if (item.subtitleKey) {
        navItem.subtitle = this._translocoService.translate(item.subtitleKey);
      }
    });

    navComponent.refresh();
  }
}
